import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Grid, Typography, Button, Card, CardHeader, CardContent, Chip, Box, Collapse, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { deleteClientDocument, requestClientDocumentCopy, deleteClientDocumentChange } from '../redux/ducks/client';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import { showAlert } from '../redux/slices/alerts';
import { useHasPermission } from '../utils/permissionUtils';
import { useIsLocked } from '../utils/lockUtils';
import ConfirmDialog from './admin/ConfirmDialog';
import ClientDocumentDialog, {
    ClientDocumentChangeDialog,
    DocumentCancelChip,
    DocumentExpiryChip,
    documentChangeFileUrl,
    documentFileUrl,
    documentResponsibleName,
    documentScopeLabel,
    documentSignerName,
    expiryActionLabel,
    isDocumentCancelled,
    splitDocumentChanges,
} from './ClientDocumentDialog';
import { formatDate, formatDayShort } from '../utils/dateUtils';

const StyledCard = styled(Card)(({ theme }) => ({
    boxShadow: theme.shadows[3],
}));

const Document = styled(Card, { shouldForwardProp: (prop) => prop !== 'cancelled' })(({ theme, cancelled }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2, 2, 1, 2),
    marginBottom: theme.spacing(2),
    ...(cancelled && { color: theme.palette.text.secondary }),
}));

const OwnerDocument = styled(Card)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2, 2, 1, 2),
    marginBottom: theme.spacing(2),
}));

const OwnerDocumentRow = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
});

const DocumentHeading = styled(Box)({
    width: '100%',
    minWidth: 0,
    overflowWrap: 'anywhere',
});

const DocumentChips = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    width: '100%',
    minWidth: 0,
}));

const ScopeChip = styled(Chip)({
    maxWidth: '100%',
});

const DocumentRowDates = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
}));

const TermsText = styled(Typography, { shouldForwardProp: (prop) => prop !== 'collapsed' })(({ collapsed }) => ({
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    width: '100%',
    ...(collapsed && {
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    }),
}));

const TermsToggle = styled(Button)({
    padding: 0,
    minWidth: 0,
    textTransform: 'none',
});

const DocumentRowRight = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
}));

const HeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(1),
}));

const CHANGE_TONES = {
    current: (theme) => theme.palette.success.main,
    upcoming: (theme) => theme.palette.info.main,
    history: (theme) => theme.palette.divider,
};

const ChangeBox = styled(Box, { shouldForwardProp: (prop) => prop !== 'tone' })(({ theme, tone }) => ({
    width: '100%',
    minWidth: 0,
    borderLeft: `3px solid ${(CHANGE_TONES[tone] || CHANGE_TONES.history)(theme)}`,
    paddingLeft: theme.spacing(1.5),
}));

const ChangeHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    minHeight: theme.spacing(3.5),
}));

const ChangeActions = styled(Box)({
    display: 'flex',
    flexWrap: 'nowrap',
});

const ChangeList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
}));

const HistoryList = styled(ChangeList)(({ theme }) => ({
    paddingTop: theme.spacing(1),
}));

const ChangeEntry = ({ label, text, change, tone, clientId, docId, canEdit, onEdit, onDelete }) => (
    <ChangeBox tone={tone}>
        <ChangeHeader>
            <Typography variant="caption" color="text.secondary">
                {change?.privacy && change.privacy !== 'Public' ? `${label} · ${change.privacy}` : label}
            </Typography>
            {change && (
                <ChangeActions>
                    {change.attachment && (
                        <>
                            <Tooltip title="View file">
                                <IconButton size="small" href={documentChangeFileUrl(clientId, docId, change._id)} target="_blank" rel="noopener">
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Download file">
                                <IconButton size="small" href={documentChangeFileUrl(clientId, docId, change._id, true)}>
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {canEdit && (
                        <>
                            <Tooltip title="Edit change">
                                <IconButton size="small" onClick={() => onEdit(change)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove change">
                                <IconButton size="small" onClick={() => onDelete(change)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </ChangeActions>
            )}
        </ChangeHeader>
        <DocumentTerms terms={text} />
    </ChangeBox>
);

const DocumentTermsSection = ({ document, clientId, canEdit, onEditChange, onDeleteChange }) => {
    const [historyOpen, setHistoryOpen] = useState(false);
    const { past, upcoming, inEffect } = splitDocumentChanges(document);
    const superseded = past.filter((change) => change !== inEffect);
    const entryProps = { clientId, docId: document._id, canEdit, onEdit: onEditChange, onDelete: onDeleteChange };
    const historyCount = superseded.length + (document.terms ? 1 : 0);
    const hasHistory = !!inEffect && historyCount > 0;
    return (
        <>
            {inEffect ? (
                <ChangeEntry {...entryProps} tone="current" label={`In effect since ${formatDayShort(inEffect.effectiveDate)}`} text={inEffect.summary} change={inEffect} />
            ) : (
                document.terms && <DocumentTerms terms={document.terms} />
            )}
            {upcoming.length > 0 && (
                <ChangeList>
                    {upcoming.map((change) => (
                        <ChangeEntry {...entryProps} key={change._id} tone="upcoming" label={`Upcoming, effective ${formatDayShort(change.effectiveDate)}`} text={change.summary} change={change} />
                    ))}
                </ChangeList>
            )}
            {hasHistory && (
                <>
                    <TermsToggle size="small" onClick={() => setHistoryOpen((v) => !v)}>
                        {historyOpen ? 'Hide history' : `Show history (${historyCount})`}
                    </TermsToggle>
                    <Collapse in={historyOpen} timeout="auto" unmountOnExit>
                        <HistoryList>
                            {document.terms && <ChangeEntry tone="history" label="Original terms" text={document.terms} />}
                            {superseded.map((change) => (
                                <ChangeEntry {...entryProps} key={change._id} tone="history" label={`Effective ${formatDayShort(change.effectiveDate)}`} text={change.summary} change={change} />
                            ))}
                        </HistoryList>
                    </Collapse>
                </>
            )}
        </>
    );
};

const DocumentTerms = ({ terms }) => {
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const ref = useRef(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (el && !expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [terms, expanded]);

    return (
        <>
            <TermsText ref={ref} variant="body2" color="text.secondary" collapsed={!expanded}>
                {terms}
            </TermsText>
            {(overflowing || expanded) && (
                <TermsToggle size="small" onClick={() => setExpanded((v) => !v)}>
                    {expanded ? 'Show less' : 'Show more'}
                </TermsToggle>
            )}
        </>
    );
};

const OwnerDocumentList = ({ documents, clientId }) => {
    const dispatch = useDispatch();
    const [pendingCopyId, setPendingCopyId] = useState(null);
    const docs = Array.isArray(documents) ? documents : [];

    const handleRequestCopy = (doc) => {
        setPendingCopyId(doc._id);
        dispatch(
            requestClientDocumentCopy(clientId, doc._id, (result) => {
                setPendingCopyId(null);
                if (result) {
                    dispatch(showAlert({ severity: 'success', message: 'Copy request sent to managers.' }));
                }
            }),
        );
    };

    return (
        <StyledCard p={{ xs: 3, md: 4, lg: 5 }}>
            <CardHeader title="Signed Documents" />
            <CardContent>
                <Grid>
                    {docs.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No documents on file.
                        </Typography>
                    )}
                    {docs.map((document) => (
                        <OwnerDocument key={document._id || document.document}>
                            <OwnerDocumentRow>
                                <Typography variant="body1">{document.document}</Typography>
                                <Chip size="small" label={document.signed ? 'Signed' : 'Not Signed'} color={document.signed ? 'success' : 'error'} />
                            </OwnerDocumentRow>
                            <DocumentRowDates>
                                <Typography variant="caption">{document.signedBy?.fullName ? `Signed by ${document.signedBy.fullName}` : '\u00a0'}</Typography>
                                <Typography variant="caption">{document.signedDate ? `Signed on ${formatDate(document.signedDate)}` : '\u00a0'}</Typography>
                            </DocumentRowDates>
                            <DocumentRowRight>
                                <Button size="small" color="primary" disabled={pendingCopyId === document._id} onClick={() => handleRequestCopy(document)}>
                                    {pendingCopyId === document._id ? 'Sending…' : 'Request a copy'}
                                </Button>
                            </DocumentRowRight>
                        </OwnerDocument>
                    ))}
                </Grid>
            </CardContent>
        </StyledCard>
    );
};

const ClientDocumentList = (props) => {
    const hasPermission = useHasPermission();
    return hasPermission('canSeeInternal') ? <StaffDocumentList {...props} /> : <OwnerDocumentList {...props} />;
};

const StaffDocumentList = ({ documents, clientId, client }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canManageDocuments = hasPermission('canManageDocuments');
    const isLocked = useIsLocked(client, null);

    const [docs, setDocs] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);
    const [pendingCopyId, setPendingCopyId] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [changeDialog, setChangeDialog] = useState(null);
    const [pendingChangeDelete, setPendingChangeDelete] = useState(null);

    useEffect(() => {
        setDocs(Array.isArray(documents) ? documents : []);
    }, [documents]);

    const refresh = () => dispatch(triggerRefresh({ triggerName: 'client' }));

    const handleOpenAdd = () => {
        setEditingDoc(null);
        setDialogOpen(true);
    };
    const handleOpenEdit = (doc) => {
        setEditingDoc(doc);
        setDialogOpen(true);
    };

    const handleSaved = () => {
        setDialogOpen(false);
        refresh();
    };

    const confirmDelete = () => {
        const doc = pendingDelete;
        setPendingDelete(null);
        if (!doc) return;
        dispatch(
            deleteClientDocument(clientId, doc._id, (result) => {
                if (result) refresh();
            }),
        );
    };

    const handleChangeSaved = () => {
        setChangeDialog(null);
        refresh();
    };

    const confirmChangeDelete = () => {
        const target = pendingChangeDelete;
        setPendingChangeDelete(null);
        if (!target) return;
        dispatch(
            deleteClientDocumentChange(clientId, target.document._id, target.change._id, (result) => {
                if (result) refresh();
            }),
        );
    };

    const handleRequestCopy = (doc) => {
        setPendingCopyId(doc._id);
        dispatch(
            requestClientDocumentCopy(clientId, doc._id, (result) => {
                setPendingCopyId(null);
                if (result) {
                    dispatch(showAlert({ severity: 'success', message: 'Copy request sent to managers.' }));
                }
            }),
        );
    };

    return (
        <>
            <StyledCard p={{ xs: 3, md: 4, lg: 5 }}>
                <CardHeader title="Documents" />
                {canManageDocuments && (
                    <HeaderActions>
                        <Button size="small" startIcon={<AddIcon />} onClick={handleOpenAdd} variant="outlined" disabled={isLocked}>
                            Add Document
                        </Button>
                    </HeaderActions>
                )}
                <CardContent>
                    <Grid>
                        {docs.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No documents on file.
                            </Typography>
                        )}
                        {docs.map((document) => {
                            const signer = documentSignerName(document);
                            const scopeLabel = documentScopeLabel(document.scope);
                            const responsibleName = documentResponsibleName(document);
                            const atExpiry = expiryActionLabel(document.expiryAction);
                            const canEdit = canManageDocuments && !isLocked;
                            const cancelled = isDocumentCancelled(document);
                            return (
                                <Document key={document._id || document.document} cancelled={cancelled}>
                                    <DocumentHeading>
                                        <Typography variant="body1">{document.document}</Typography>
                                        {document.title && (
                                            <Typography variant="body2" color="text.secondary">
                                                {document.title}
                                            </Typography>
                                        )}
                                    </DocumentHeading>
                                    <DocumentChips>
                                        {(document.signed || !cancelled) && <Chip size="small" label={document.signed ? 'Signed' : 'Not Signed'} color={document.signed ? 'success' : 'error'} />}
                                        <DocumentCancelChip document={document} />
                                        <DocumentExpiryChip document={document} />
                                        {scopeLabel && (
                                            <Tooltip title={scopeLabel}>
                                                <ScopeChip size="small" variant="outlined" label={scopeLabel} />
                                            </Tooltip>
                                        )}
                                    </DocumentChips>
                                    <DocumentRowDates>
                                        <Typography variant="caption">{signer ? `Signed by ${signer}` : ' '}</Typography>
                                        <Typography variant="caption">{document.signedDate ? `Signed on ${formatDate(document.signedDate)}` : ' '}</Typography>
                                    </DocumentRowDates>
                                    {(responsibleName || atExpiry) && (
                                        <DocumentRowDates>
                                            <Typography variant="caption">{responsibleName ? `Responsible: ${responsibleName}` : ' '}</Typography>
                                            <Typography variant="caption">{atExpiry ? `At expiry: ${atExpiry}` : ' '}</Typography>
                                        </DocumentRowDates>
                                    )}
                                    <DocumentTermsSection
                                        document={document}
                                        clientId={clientId}
                                        canEdit={canEdit}
                                        onEditChange={(change) => setChangeDialog({ document, change })}
                                        onDeleteChange={(change) => setPendingChangeDelete({ document, change })}
                                    />
                                    <DocumentRowRight>
                                        {document.attachment ? (
                                            <>
                                                <Button size="small" startIcon={<OpenInNewIcon />} href={documentFileUrl(clientId, document._id)} target="_blank" rel="noopener">
                                                    View
                                                </Button>
                                                <Button size="small" startIcon={<DownloadIcon />} href={documentFileUrl(clientId, document._id, true)}>
                                                    Download
                                                </Button>
                                            </>
                                        ) : (
                                            <Button size="small" color="primary" disabled={pendingCopyId === document._id} onClick={() => handleRequestCopy(document)}>
                                                {pendingCopyId === document._id ? 'Sending…' : 'Request a copy'}
                                            </Button>
                                        )}
                                        {canEdit && (
                                            <>
                                                <Button size="small" startIcon={<AddIcon />} onClick={() => setChangeDialog({ document, change: null })}>
                                                    Add change
                                                </Button>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleOpenEdit(document)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Remove">
                                                    <IconButton size="small" onClick={() => setPendingDelete(document)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </DocumentRowRight>
                                </Document>
                            );
                        })}
                    </Grid>
                </CardContent>
            </StyledCard>
            {canManageDocuments && (
                <ClientDocumentDialog
                    open={dialogOpen}
                    document={editingDoc}
                    clientId={clientId}
                    onClose={() => setDialogOpen(false)}
                    onSaved={handleSaved}
                />
            )}
            {canManageDocuments && (
                <ClientDocumentChangeDialog
                    open={Boolean(changeDialog)}
                    clientId={clientId}
                    document={changeDialog?.document}
                    change={changeDialog?.change}
                    onClose={() => setChangeDialog(null)}
                    onSaved={handleChangeSaved}
                />
            )}
            <ConfirmDialog
                open={Boolean(pendingChangeDelete)}
                onClose={() => setPendingChangeDelete(null)}
                onConfirm={confirmChangeDelete}
                title="Remove change"
                confirmLabel="Remove"
                message={pendingChangeDelete ? `Remove the change effective ${formatDayShort(pendingChangeDelete.change.effectiveDate)}? This cannot be undone.` : ''}
            />
            <ConfirmDialog
                open={Boolean(pendingDelete)}
                onClose={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
                title="Remove document"
                confirmLabel="Remove"
                message={pendingDelete ? `Remove "${[pendingDelete.document, pendingDelete.title].filter(Boolean).join(' - ')}"? This cannot be undone.` : ''}
            />
        </>
    );
};

export default ClientDocumentList;
